/**
 * Developed by Spell Solutions for Trivinci Systems, LLC.
 *
 * Developer attribution only. Ownership, licensing, and permitted use are
 * governed by the applicable agreement and applicable law.
 */
import { LitElement, css, html, svg } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import type { TelemetryLatest } from "../services/telemetry";
import { TELEMETRY_STALE_MS } from "../services/config";
import { ReverseGeocoder, formatCoords } from "../services/geocode";

/**
 * <rk-hud variant="video|panel|tile|mobile">
 *
 * Mercedes-AMG inspired cluster:
 *   [ Speed dial │ Center info strip │ RPM dial ]
 *
 * Each dial: outer ring with numbered tick marks, inner gradient arc fill,
 * digital value floating in the center, label below. Soft 3D shadows give
 * each dial the feel of a polished disc.
 *
 * The center info strip carries Throttle, Fuel, Coolant, GPS — the
 * "secondary cluster" Mercedes places between the two big gauges.
 *
 * Telemetry writes go straight into DOM refs; Lit never re-renders on the
 * 200 ms tick.
 */

type Variant = "video" | "panel" | "tile" | "mobile";

interface Tween {
  target: HTMLElement;
  fromVal: number;
  toVal: number;
  start: number;
  duration: number;
  fmt: (v: number) => string;
  key: string;
}

const MAX_RPM = 9000;
const MAX_SPEED = 160;
const REDLINE_RPM = 7000; // start of red zone

/** Dial geometry — viewBox 0 0 140 140, centre 70,70.
 *  Layout, outer → inner:
 *    rGlow (66) ── glowing outer halo (filled circle with blur)
 *    rBezel (61) ── thin bezel ring
 *    rTickMajorOuter (60) / rTickOuter (58) / rTickInner (54) ── tick marks
 *    rNumber (46) ── tick number labels
 *    rArc (40) ── inner coloured arc (track + fill)
 *    rFace (34) ── recessed dial face (gradient backdrop) for the digit
 */
const DIAL = {
  cx: 70,
  cy: 70,
  rGlow: 66,
  rBezel: 61,
  rTickMajorOuter: 60,
  rTickOuter: 58,
  rTickInner: 54,
  rNumber: 47,
  rArc: 40,
  rFace: 34,
  rNeedlePivot: 6,
  rNeedleTip: 50,
  /** Total arc sweep: 270° (gap at bottom). */
  span: 270,
  /** Start at -135° (bottom-left), end +135° (bottom-right). */
  start: -135,
};

/** Circumference of full circle for r=50 → 2π·50 = 314.16. */
const FULL_CIRC = 2 * Math.PI * DIAL.rArc;
/** 270° arc = 3/4 of full circumference. */
const ARC_LEN = (DIAL.span / 360) * FULL_CIRC;

/** Convert angle in degrees (0 = up, clockwise) into x,y on circle of radius r. */
function polar(r: number, deg: number): [number, number] {
  const a = ((deg - 90) * Math.PI) / 180;
  return [DIAL.cx + r * Math.cos(a), DIAL.cy + r * Math.sin(a)];
}

/** Build a 270° arc path string from start angle to end angle clockwise. */
function arcPath(rad: number, fromDeg: number, toDeg: number): string {
  const [x1, y1] = polar(rad, fromDeg);
  const [x2, y2] = polar(rad, toDeg);
  const large = Math.abs(toDeg - fromDeg) > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${rad} ${rad} 0 ${large} 1 ${x2} ${y2}`;
}

@customElement("rk-hud")
export class RkHud extends LitElement {
  static styles = css`
    :host {
      display: block;
      color: var(--rk-text);
      font-family: var(--rk-font-sans);
      contain: layout style paint;
    }

    /* ── Card frame ────────────────────────────────────────────────────── */
    /* 90% transparent: barely there dark wash + faint blur so legibility
       holds when behind bright video, but the video remains the hero. */
    .card {
      position: relative;
      display: grid;
      grid-template-columns: auto auto auto;
      align-items: center;
      gap: 14px;
      padding: 10px 14px 12px;
      background:
        radial-gradient(120% 200% at 0% 50%, rgba(56, 189, 248, 0.04), transparent 55%),
        radial-gradient(120% 200% at 100% 50%, rgba(56, 189, 248, 0.04), transparent 55%),
        linear-gradient(180deg, rgba(8, 11, 18, 0.08), rgba(8, 11, 18, 0.12));
      backdrop-filter: blur(4px) saturate(120%);
      -webkit-backdrop-filter: blur(4px) saturate(120%);
      border-radius: 22px;
      box-shadow:
        0 24px 60px -16px rgba(0, 0, 0, 0.45),
        0 1px 0 rgba(255, 255, 255, 0.05) inset;
      isolation: isolate;
    }
    .card::before {
      content: "";
      position: absolute;
      inset: 0;
      padding: 1px;
      border-radius: inherit;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.24) 0%,
        rgba(255, 255, 255, 0.04) 35%,
        rgba(56, 189, 248, 0.16) 100%
      );
      -webkit-mask:
        linear-gradient(#000 0 0) content-box,
        linear-gradient(#000 0 0);
      -webkit-mask-composite: xor;
      mask:
        linear-gradient(#000 0 0) content-box,
        linear-gradient(#000 0 0);
      mask-composite: exclude;
      pointer-events: none;
    }
    .card[data-stale="true"]::before {
      background: linear-gradient(
        135deg,
        rgba(251, 113, 133, 0.45) 0%,
        rgba(255, 255, 255, 0.04) 60%,
        rgba(251, 113, 133, 0.25) 100%
      );
    }

    /* ── Live-tick indicator ───────────────────────────────────────────── */
    .tick-dot {
      position: absolute;
      top: 10px;
      right: 14px;
      width: 7px;
      height: 7px;
      border-radius: 999px;
      background: rgba(34, 211, 238, 0.85);
      box-shadow: 0 0 8px rgba(34, 211, 238, 0.85);
      transition: opacity 600ms ease-out, transform 600ms ease-out;
      opacity: 0.3;
      transform: scale(0.85);
      pointer-events: none;
      z-index: 2;
    }
    .tick-dot.on {
      opacity: 1;
      transform: scale(1.15);
      transition: opacity 80ms ease-out, transform 80ms ease-out;
    }
    .card[data-stale="true"] .tick-dot {
      background: rgba(251, 113, 133, 0.85);
      box-shadow: 0 0 8px rgba(251, 113, 133, 0.85);
    }

    /* ── Dials ─────────────────────────────────────────────────────────── */
    .dial {
      position: relative;
      width: 168px;
      height: 168px;
      flex: none;
      filter: drop-shadow(0 10px 22px rgba(0, 0, 0, 0.55));
    }
    .dial svg {
      width: 100%;
      height: 100%;
      display: block;
      overflow: visible;
    }
    /* Outer glow halo (radial fade) */
    .dial .halo {
      fill: url(#rk-halo-grad);
      opacity: 0.95;
    }
    /* Thin outer bezel — like an LED ring */
    .dial .bezel {
      fill: none;
      stroke: rgba(125, 211, 252, 0.4);
      stroke-width: 0.8;
      filter: drop-shadow(0 0 4px rgba(56, 189, 248, 0.55));
    }
    /* Recessed inner face — subtle radial gradient (darker centre) */
    .dial .face {
      fill: url(#rk-face-grad);
    }
    .dial .face-rim {
      fill: none;
      stroke: rgba(255, 255, 255, 0.08);
      stroke-width: 0.6;
    }

    .dial .tick {
      stroke: rgba(255, 255, 255, 0.55);
      stroke-width: 1.6;
      stroke-linecap: round;
    }
    .dial .tick-major {
      stroke: rgba(255, 255, 255, 0.95);
      stroke-width: 2.2;
    }
    .dial .tick-red {
      stroke: rgba(244, 63, 94, 0.92);
    }
    .dial .tick-major.tick-red {
      stroke: rgba(244, 63, 94, 1);
      filter: drop-shadow(0 0 3px rgba(244, 63, 94, 0.6));
    }
    .dial .num {
      fill: rgba(255, 255, 255, 0.85);
      font-family: var(--rk-font-sans);
      font-size: 9.5px;
      font-weight: 700;
      text-anchor: middle;
      dominant-baseline: middle;
      letter-spacing: 0;
    }
    .dial .num-red {
      fill: rgba(244, 63, 94, 1);
      filter: drop-shadow(0 0 3px rgba(244, 63, 94, 0.5));
    }
    .dial .track {
      fill: none;
      stroke: rgba(255, 255, 255, 0.06);
      stroke-width: 7;
      stroke-linecap: round;
    }
    .dial .fill {
      fill: none;
      stroke-width: 7;
      stroke-linecap: round;
      stroke-dasharray: 0 999;
      transition: stroke-dasharray var(--rk-dur-base) var(--rk-ease-out);
      filter: drop-shadow(0 0 8px rgba(56, 189, 248, 0.7));
    }
    .dial[data-key="speed"] .fill { stroke: url(#rk-grad-speed); }
    .dial[data-key="rpm"] .fill { stroke: url(#rk-grad-rpm); }

    .dial-text {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0;
      pointer-events: none;
    }
    .dial-val {
      font-family: var(--rk-font-sans);
      font-weight: 800;
      font-size: 28px;
      line-height: 1;
      letter-spacing: -1px;
      font-variant-numeric: tabular-nums;
      background: linear-gradient(180deg, #ffffff 0%, #d4ecff 55%, #7dd3fc 100%);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      filter: drop-shadow(0 1px 8px rgba(125, 211, 252, 0.35));
      margin-top: 0;
    }
    .card[data-stale="true"] .dial-val {
      background: linear-gradient(180deg, #fff 0%, #fecdd3 60%, #fb7185 100%);
      -webkit-background-clip: text;
      background-clip: text;
    }
    .dial-key {
      font-family: var(--rk-font-mono);
      font-size: 9px;
      letter-spacing: 0.22em;
      color: rgba(255, 255, 255, 0.5);
      text-transform: uppercase;
      margin-top: 2px;
    }

    /* ── Center cluster strip ──────────────────────────────────────────── */
    .center {
      display: grid;
      grid-template-rows: 1fr 1fr 1fr 1fr;
      gap: 4px;
      align-self: stretch;
      padding: 6px 6px;
      min-width: 0;
      width: 260px;          /* keep cluster compact; HUD shrinks to content */
    }
    .row {
      display: grid;
      grid-template-columns: 14px 64px 1fr auto;
      align-items: center;
      gap: 7px;
      padding: 1px 0;
      min-width: 0;
    }
    .row .icon {
      width: 14px;
      height: 14px;
      color: rgba(255, 255, 255, 0.68);
      flex: none;
    }
    .row .lbl {
      font-family: var(--rk-font-mono);
      font-size: 9px;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: rgba(255, 255, 255, 0.6);
    }
    .row .bar {
      position: relative;
      height: 5px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.06);
      overflow: hidden;
      min-width: 0;
    }
    .row .bar::before {
      content: "";
      position: absolute;
      inset: 0;
      width: var(--w, 0%);
      border-radius: inherit;
      background: var(--c, rgba(125, 211, 252, 0.92));
      box-shadow: 0 0 6px var(--c-glow, rgba(56, 189, 248, 0.45));
      transition: width var(--rk-dur-base) var(--rk-ease-out);
    }
    .row .val {
      font-weight: 700;
      font-size: 12px;
      font-variant-numeric: tabular-nums;
      color: var(--rk-text);
      min-width: 40px;
      text-align: right;
      white-space: nowrap;
    }
    .row .val .u {
      font-size: 9px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.55);
      margin-left: 1px;
    }
    .row[data-key="throttle"] .bar { --c: rgba(125, 211, 252, 0.92); --c-glow: rgba(56, 189, 248, 0.45); }
    .row[data-key="fuel"]     .bar { --c: rgba(74, 222, 128, 0.92);  --c-glow: rgba(34, 197, 94, 0.45); }
    .row[data-key="coolant"]  .bar { --c: rgba(251, 191, 36, 0.92);  --c-glow: rgba(251, 191, 36, 0.45); }

    .row[data-key="fuel"][data-warn="true"] .icon,
    .row[data-key="fuel"][data-warn="true"] .val,
    .row[data-key="coolant"][data-warn="true"] .icon,
    .row[data-key="coolant"][data-warn="true"] .val { color: var(--rk-bad); }

    .row.gps { grid-template-columns: 14px 36px 1fr; }
    .row.gps .coords {
      font-family: var(--rk-font-mono);
      font-size: 10.5px;
      color: var(--rk-text);
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
      text-align: left;
    }
    .row.gps .pill {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 2px 8px;
      border-radius: 999px;
      font-family: var(--rk-font-mono);
      font-size: 9px;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      background: rgba(34, 211, 238, 0.12);
      color: var(--rk-live);
      border: 1px solid rgba(34, 211, 238, 0.32);
      flex-shrink: 0;
    }
    .card[data-stale="true"] .row.gps .pill {
      background: rgba(251, 113, 133, 0.12);
      color: var(--rk-bad);
      border-color: rgba(251, 113, 133, 0.32);
    }

    /* ── Variants ──────────────────────────────────────────────────────── */

    /* Panel: dials side-by-side on top, cluster strip beneath. The HTML
       order is [speed, center, rpm], so we explicitly place each item
       on the grid — otherwise center (which spans both columns) breaks
       the dials apart vertically. */
    :host([variant="panel"]) .card {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto auto;
      gap: 12px;
      padding: 12px;
      background:
        radial-gradient(80% 100% at 50% 0%, rgba(56, 189, 248, 0.04), transparent 70%),
        linear-gradient(180deg, rgba(8, 11, 18, 0.08), rgba(8, 11, 18, 0.14));
      backdrop-filter: blur(4px);
      border-radius: 14px;
    }
    :host([variant="panel"]) .card::before { display: none; }
    :host([variant="panel"]) .dial { width: 130px; height: 130px; margin: 0 auto; }
    :host([variant="panel"]) .dial[data-key="speed"] { grid-column: 1; grid-row: 1; }
    :host([variant="panel"]) .dial[data-key="rpm"]   { grid-column: 2; grid-row: 1; }
    :host([variant="panel"]) .dial-val { font-size: 26px; }
    :host([variant="panel"]) .dial-key { font-size: 8px; }
    :host([variant="panel"]) .center {
      grid-column: 1 / -1;
      grid-row: 2;
      grid-template-rows: repeat(4, auto);
      padding: 8px 2px 4px;
      width: auto;
      gap: 6px;
      border-top: 1px solid rgba(255, 255, 255, 0.06);
    }
    :host([variant="panel"]) .row {
      grid-template-columns: 14px 64px 1fr auto;
      gap: 8px;
    }
    :host([variant="panel"]) .row .lbl { font-size: 9px; }
    :host([variant="panel"]) .row .val { font-size: 12px; min-width: 44px; }
    :host([variant="panel"]) .row.gps .coords { font-size: 11px; }

    /* Tile: just speed dial */
    :host([variant="tile"]) .card {
      grid-template-columns: 1fr;
      gap: 0;
      padding: 6px 8px;
      background: linear-gradient(180deg, rgba(6, 8, 13, 0) 0%, rgba(6, 8, 13, 0.82) 100%);
      backdrop-filter: none;
      box-shadow: none;
      border-radius: 12px;
    }
    :host([variant="tile"]) .card::before { display: none; }
    :host([variant="tile"]) .dial { width: 90px; height: 90px; margin: 0 auto; }
    :host([variant="tile"]) .dial-val { font-size: 22px; }
    :host([variant="tile"]) .dial-key { font-size: 7px; }
    :host([variant="tile"]) .dial[data-key="rpm"],
    :host([variant="tile"]) .center { display: none; }

    /* ── Mobile (≤ 720px) — kvs20 single-row layout ──────────────────
       The desktop dual-dial .card is hidden on mobile; in its place the
       .hud-mobile block renders the original kvs20 layout:
         ┌──────────────────────────────────────────────────────┐
         │ SPEED      ╭──────╮     Throttle: 38%                │
         │  42  mph   │  RPM │     Fuel:     74%                │
         │  [STALE]   │ ⤴   │     Coolant:  82°C               │
         │            │ 7032 │     GPS: 29.73, -95.85           │
         │            ╰──────╯                                  │
         └──────────────────────────────────────────────────────┘
       Default desktop styles: .hud-mobile is display:none. The mobile
       block becomes visible only at the breakpoint below.
    */
    .hud-mobile { display: none; }

    /* Mobile breakpoint: catch BOTH narrow-width devices (portrait phones)
       and short-height landscape phones. A phone in landscape is often
       wider than 720px (e.g., 844×390 on iPhone 14), so max-width alone
       would leave the desktop dual-dial HUD on screen — too tall for the
       ~390px landscape viewport. The (orientation: landscape) clause with
       max-height 500px catches landscape phones but excludes iPads and
       laptops (which are all > 500px tall in landscape). */
    @media (max-width: 720px),
           (orientation: landscape) and (max-height: 500px) {
      :host([variant="video"]) .card { display: none; }
      :host([variant="video"]) .hud-mobile { display: block; }
    }

    /* Mobile HUD card — kvs20 deployed visual style.
       Transparent background + thin border + soft drop shadow (no dark fill,
       no blur). Width is capped well under the full screen width on landscape
       phones so the HUD doesn't sprawl, and centred via the parent flex
       container in rk-player. */
    :host([variant="video"]) .hud-mobile {
      width: 100%;
      max-width: 720px;
      box-sizing: border-box;
      padding: 7px 10px;
      border-radius: 18px;
      border: 1px solid rgba(255, 255, 255, 0.12);
      background: transparent;
      box-shadow: 0 18px 60px rgba(0, 0, 0, 0.55);
    }
    @media (orientation: landscape) and (max-width: 900px) {
      :host([variant="video"]) .hud-mobile { padding: 6px 10px; }
    }

    /* Landscape phones: the mobile HUD is a fixed ~107px tall whatever the
       device, so on a short screen it takes a disproportionate share — 28.5%
       of the height on a 375px landscape phone, before the rear inset takes
       its own cut. The client reported having little front camera left to
       watch.

       Scaled rather than restyled. The bottom bar is absolutely positioned
       over the video, so shrinking the HUD hands that space straight back as
       visible picture, and a uniform scale keeps every proportion — most
       visibly the speed dial, which stays circular instead of being squashed
       by a height cap. Anchored to the bottom so it shrinks away from the
       video rather than lifting off the bottom edge.

       0.68 puts it near 73px, under a fifth of the screen, with the speed
       readout still about 20px — the one number that has to be readable at a
       glance mid-lap. */
    @media (orientation: landscape) and (max-height: 500px) {
      :host([variant="video"]) .hud-mobile {
        transform: scale(0.68);
        transform-origin: bottom center;
      }
    }
    .m-row {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
      justify-content: flex-start;
    }
    /* Speed block (text only) */
    .m-speed-block {
      width: 102px;
      min-width: 102px;
      display: grid;
      row-gap: 2px;
    }
    @media (orientation: landscape) and (max-width: 900px) {
      .m-speed-block { width: 112px; min-width: 112px; }
    }
    .m-speed-label {
      font-size: 12px;
      font-weight: 650;
      letter-spacing: 0.06em;
      color: rgba(255, 255, 255, 0.58);
      font-family: var(--rk-font-sans);
    }
    .m-speed-line {
      display: flex;
      align-items: baseline;
      gap: 8px;
    }
    .m-speed-val {
      font-size: 32px;
      font-weight: 800;
      line-height: 1;
      letter-spacing: -0.15px;
      font-variant-numeric: tabular-nums;
      color: rgba(255, 255, 255, 0.92);
      font-family: var(--rk-font-sans);
    }
    @media (orientation: landscape) and (max-width: 900px) {
      .m-speed-val { font-size: 30px; }
    }
    .m-speed-unit {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.68);
    }
    .m-stale-pill {
      margin-top: 2px;
      font-size: 10.5px;
      font-weight: 700;
      padding: 3px 8px;
      width: fit-content;
      border-radius: 999px;
      border: 1px solid rgba(251, 113, 133, 0.35);
      background: rgba(251, 113, 133, 0.10);
      color: rgba(255, 255, 255, 0.84);
      letter-spacing: 0.04em;
    }
    .m-stale-pill[hidden] { display: none; }

    /* RPM gauge (custom SVG: half-circle arc + needle + value text below) */
    .m-rpm-block {
      flex: 0 0 auto;
      display: grid;
      justify-items: center;
      min-width: 0;
    }
    .m-rpm-svg {
      width: 130px;
      height: 72px;          /* 130 * 100 / 180 — matches kvs20 viewBox aspect */
      display: block;
    }
    @media (orientation: landscape) and (max-width: 900px) {
      .m-rpm-svg { width: 128px; height: 71px; }
    }
    .m-rpm-arc-bg {
      fill: none;
      stroke: rgba(255, 255, 255, 0.18);
      stroke-width: 10;
      stroke-linecap: round;
    }
    .m-rpm-arc-fill {
      fill: none;
      stroke: rgba(74, 222, 128, 0.70);
      stroke-width: 10;
      stroke-linecap: round;
      stroke-dasharray: 0 999;
      transition: stroke-dasharray var(--rk-dur-base) var(--rk-ease-out);
    }
    .m-rpm-needle {
      stroke: rgba(255, 255, 255, 0.88);
      stroke-width: 3.2;
      stroke-linecap: round;
      transform-origin: 90px 92px;
      transition: transform var(--rk-dur-base) var(--rk-ease-out);
    }
    .m-rpm-needle-group {
      transition: transform var(--rk-dur-base) var(--rk-ease-out);
    }
    .m-rpm-dot { fill: rgba(255, 255, 255, 0.86); }
    .m-rpm-text {
      margin-top: 6px;
      font-size: 11px;
      letter-spacing: 0.06em;
      color: rgba(255, 255, 255, 0.68);
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
      font-family: var(--rk-font-mono);
    }

    /* Labeled telemetry rows (Throttle / Fuel / Coolant / GPS) */
    .m-rows {
      flex: 1 1 auto;
      min-width: 0;
      display: grid;
      row-gap: 7px;
    }
    @media (orientation: landscape) and (max-width: 900px) {
      .m-rows { row-gap: 6px; }
    }
    .m-tele-row {
      display: flex;
      align-items: baseline;
      gap: 6px;
      min-width: 0;
    }
    .m-tele-label {
      width: 62px;
      font-size: 11.25px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.70);
      font-family: var(--rk-font-sans);
    }
    @media (orientation: landscape) and (max-width: 900px) {
      .m-tele-label { width: 68px; }
    }
    .m-tele-val {
      flex: 1;
      min-width: 0;
      font-size: 11.25px;
      font-weight: 420;
      color: rgba(255, 255, 255, 0.82);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-variant-numeric: tabular-nums;
      font-family: var(--rk-font-mono);
    }
  `;

  @property({ reflect: true }) variant: Variant = "video";

  @query(".card") private cardEl!: HTMLDivElement;
  @query(".tick-dot") private tickEl?: HTMLDivElement;
  @query("#speed-val") private speedValEl!: HTMLDivElement;
  @query("#rpm-val") private rpmValEl!: HTMLDivElement;
  @query("#speed-fill") private speedFillEl!: SVGPathElement;
  @query("#rpm-fill") private rpmFillEl!: SVGPathElement;
  @query("#throttle-val") private throttleValEl!: HTMLSpanElement;
  @query("#throttle-bar") private throttleBarEl!: HTMLDivElement;
  @query("#fuel-val") private fuelValEl!: HTMLSpanElement;
  @query("#fuel-bar") private fuelBarEl!: HTMLDivElement;
  @query("#fuel-row") private fuelRowEl!: HTMLDivElement;
  @query("#coolant-val") private coolantValEl!: HTMLSpanElement;
  @query("#coolant-bar") private coolantBarEl!: HTMLDivElement;
  @query("#coolant-row") private coolantRowEl!: HTMLDivElement;
  @query("#gps-coords") private gpsCoordsEl!: HTMLSpanElement;

  // Mobile (kvs20-style) parallel layout — populated alongside the desktop
  // elements above. Visible only at the mobile breakpoint (see CSS); the
  // desktop .card is hidden there. feed() updates both in lockstep.
  @query(".hud-mobile") private mobileEl?: HTMLDivElement;
  @query("#m-speed-val") private mSpeedValEl?: HTMLDivElement;
  @query("#m-rpm-val") private mRpmValEl?: HTMLSpanElement;
  @query("#m-rpm-arc-fill") private mRpmArcFillEl?: SVGPathElement;
  @query("#m-rpm-needle-group") private mRpmNeedleGroupEl?: SVGGElement;
  @query("#m-throttle-val") private mThrottleValEl?: HTMLDivElement;
  @query("#m-fuel-val") private mFuelValEl?: HTMLDivElement;
  @query("#m-coolant-val") private mCoolantValEl?: HTMLDivElement;
  @query("#m-gps-val") private mGpsValEl?: HTMLDivElement;
  @query("#m-stale-pill") private mStalePillEl?: HTMLDivElement;

  private geocoder = new ReverseGeocoder();
  private tweens: Tween[] = [];
  private rafHandle: number | null = null;
  private displayed: Record<string, number> = {
    speed: 0, rpm: 0, throttle: 0, fuel: 0, coolant: 0,
  };

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.rafHandle != null) cancelAnimationFrame(this.rafHandle);
  }

  feed(snap: TelemetryLatest, nowMs: number, videoLive = false): void {
    if (!this.cardEl) {
      queueMicrotask(() => this.feed(snap, nowMs));
      return;
    }
    const fetchStale = !snap.ok || nowMs - snap.receivedAtMs > TELEMETRY_STALE_MS;
    const serverStale =
      snap.lastSeenAgeS != null &&
      snap.staleThresholdS != null &&
      snap.lastSeenAgeS > snap.staleThresholdS;
    const stale = fetchStale || serverStale;
    this.cardEl.dataset.stale = String(stale);
    this.pulseTick();

    // When stale, treat every numeric reading as "missing" — the operator
    // shouldn't have to interpret "stale 11" alongside the rose pill. GPS
    // coords stay visible (the map marker uses them) but the LIVE/STALE
    // pill on the GPS row flips so it's clear they're old.
    const speedV   = stale ? null : snap.speedMph;
    const rpmV     = stale ? null : snap.rpm;
    const thrV     = stale ? null : snap.throttlePct;
    const fuelV    = stale ? null : snap.fuelPct;
    const coolantV = stale ? null : snap.coolantC;

    this.queueTween("speed", this.speedValEl, speedV, (v) =>
      v == null ? "—" : Math.max(0, Math.round(v)).toString()
    );
    this.applyArc(this.speedFillEl, speedV, MAX_SPEED);

    this.queueTween("rpm", this.rpmValEl, rpmV, (v) =>
      v == null ? "—" : Math.max(0, Math.round(v)).toString()
    );
    this.applyArc(this.rpmFillEl, rpmV, MAX_RPM);

    this.queueTween("throttle", this.throttleValEl, thrV, (v) =>
      v == null ? "—" : Math.round(v).toString()
    );
    this.applyBar(this.throttleBarEl, thrV, 100);

    this.queueTween("fuel", this.fuelValEl, fuelV, (v) =>
      v == null ? "—" : Math.round(v).toString()
    );
    this.applyBar(this.fuelBarEl, fuelV, 100);
    if (this.fuelRowEl) {
      this.fuelRowEl.dataset.warn = String(!stale && snap.fuelPct != null && snap.fuelPct < 15);
    }

    this.queueTween("coolant", this.coolantValEl, coolantV, (v) =>
      v == null ? "—" : Math.round(v).toString()
    );
    this.applyBar(this.coolantBarEl, coolantV, 120);
    if (this.coolantRowEl) {
      this.coolantRowEl.dataset.warn = String(!stale && snap.coolantC != null && snap.coolantC > 105);
    }

    if (this.gpsCoordsEl) {
      const loc = this.geocoder.label(snap.gpsLat, snap.gpsLon);
      // Fall back to the coordinates rather than an indefinite "Locating…":
      // the fix is good here, only the place-name lookup is missing.
      this.gpsCoordsEl.textContent =
        snap.gpsLat == null || snap.gpsLon == null
          ? "Waiting for fix…"
          : loc ?? formatCoords(snap.gpsLat, snap.gpsLon);
    }

    // ── Mobile (kvs20-style) parallel updates ────────────────────────────
    // Same data, painted into the second layout block which becomes visible
    // at the mobile breakpoint. Writes are cheap; the hidden block stays in
    // sync so toggling visibility never reveals stale numbers.
    if (this.mobileEl) {
      this.mobileEl.dataset.stale = String(stale);

      this.queueTween("m-speed", this.mSpeedValEl, speedV, (v) =>
        v == null ? "--" : Math.max(0, Math.round(v)).toString()
      );

      this.queueTween("m-rpm", this.mRpmValEl, rpmV, (v) =>
        v == null ? "--" : Math.max(0, Math.round(v)).toString()
      );
      this.applyMobileRpm(rpmV);

      this.queueTween("m-throttle", this.mThrottleValEl, thrV, (v) =>
        v == null ? "--" : `${Math.round(v)}%`
      );
      this.queueTween("m-fuel", this.mFuelValEl, fuelV, (v) =>
        v == null ? "--" : `${Math.round(v)}%`
      );
      this.queueTween("m-coolant", this.mCoolantValEl, coolantV, (v) =>
        v == null ? "--" : `${Math.round(v)}°C`
      );

      if (this.mGpsValEl) {
        const loc = this.geocoder.label(snap.gpsLat, snap.gpsLon);
        this.mGpsValEl.textContent =
          snap.gpsLat == null || snap.gpsLon == null
            ? "--"
            : loc ?? formatCoords(snap.gpsLat, snap.gpsLon);
      }

      if (this.mStalePillEl) {
        this.mStalePillEl.toggleAttribute("hidden", !stale);
      }
    }
  }

  /** Mobile RPM gauge: half-circle arc + rotating needle. Both are tweened
   *  via CSS transitions on the relevant SVG attributes. */
  private applyMobileRpm(value: number | null): void {
    if (!this.mRpmArcFillEl && !this.mRpmNeedleGroupEl) return;
    const v = value == null || !Number.isFinite(value) ? 0 : Math.max(0, Math.min(MAX_RPM, value));
    const norm = v / MAX_RPM;
    // Half-circle: 180° sweep, radius 70 → arc length = π·70 ≈ 219.9 px.
    const HALF_ARC_LEN = Math.PI * 70;
    if (this.mRpmArcFillEl) {
      this.mRpmArcFillEl.style.strokeDasharray = `${norm * HALF_ARC_LEN} 999`;
    }
    // Needle: -180° (RPM=0, points left) to 0° (RPM=max, points right).
    if (this.mRpmNeedleGroupEl) {
      const angle = -180 + norm * 180;
      this.mRpmNeedleGroupEl.setAttribute("transform", `rotate(${angle} 90 92)`);
    }
  }

  private pulseTick(): void {
    if (!this.tickEl) return;
    this.tickEl.classList.remove("on");
    void this.tickEl.offsetWidth;
    this.tickEl.classList.add("on");
  }

  private applyArc(el: SVGPathElement | undefined, value: number | null, max: number): void {
    if (!el) return;
    const v = value == null || !Number.isFinite(value) ? 0 : Math.max(0, Math.min(max, value));
    const arcOn = (v / max) * ARC_LEN;
    // Write the dasharray as a CSS property (inline style), not an attribute.
    // CSS `transition: stroke-dasharray …` only animates the *property* form
    // reliably; Safari in particular won't transition the SVG attribute form.
    el.style.strokeDasharray = `${arcOn} 999`;
  }


  private applyBar(el: HTMLElement | undefined, value: number | null, max: number): void {
    if (!el) return;
    if (value == null || !Number.isFinite(value)) {
      el.style.setProperty("--w", "0%");
      return;
    }
    const pct = Math.max(0, Math.min(100, (value / max) * 100));
    el.style.setProperty("--w", `${pct}%`);
  }

  private queueTween(
    key: string,
    target: HTMLElement | undefined,
    next: number | null,
    fmt: (v: number) => string
  ): void {
    if (!target) return;
    if (next == null || !Number.isFinite(next)) {
      target.textContent = "—";
      this.displayed[key] = 0;
      this.tweens = this.tweens.filter((t) => t.target !== target);
      return;
    }
    this.tweens = this.tweens.filter((t) => t.target !== target);
    this.tweens.push({
      target, key,
      fromVal: this.displayed[key] ?? next,
      toVal: next,
      start: performance.now(),
      duration: 220,
      fmt,
    });
    if (this.rafHandle == null) this.rafHandle = requestAnimationFrame(this.tick);
  }

  private tick = (t: number): void => {
    if (!this.tweens.length) {
      this.rafHandle = null;
      return;
    }
    const ease = (x: number) => 1 - Math.pow(1 - x, 3);
    const remaining: Tween[] = [];
    for (const tw of this.tweens) {
      const elapsed = t - tw.start;
      const p = Math.min(1, elapsed / tw.duration);
      const v = tw.fromVal + (tw.toVal - tw.fromVal) * ease(p);
      tw.target.textContent = tw.fmt(v);
      this.displayed[tw.key] = v;
      if (p < 1) remaining.push(tw);
      else this.displayed[tw.key] = tw.toVal;
    }
    this.tweens = remaining;
    this.rafHandle = requestAnimationFrame(this.tick);
  };

  /* ── Dial SVG renderer ────────────────────────────────────────────────
     Layered: halo → bezel → ticks/numbers → arc track/fill → face → needle.
     Major tick numbered, 4 minors in between. RPM redline ticks/numbers in red. */
  private renderDialSvg(opts: {
    key: "speed" | "rpm";
    max: number;
    majorStep: number;
    labelStep: number;
    redFrom?: number;
  }) {
    const ticks: ReturnType<typeof svg>[] = [];
    const totalSteps = Math.round(opts.max / opts.majorStep);
    for (let i = 0; i <= totalSteps; i++) {
      const value = i * opts.majorStep;
      const ratio = value / opts.max;
      const deg = DIAL.start + ratio * DIAL.span;
      const isRed = opts.redFrom != null && value >= opts.redFrom;
      const [x1, y1] = polar(DIAL.rTickInner, deg);
      const [x2, y2] = polar(DIAL.rTickMajorOuter, deg);
      ticks.push(svg`
        <line class=${`tick tick-major ${isRed ? "tick-red" : ""}`}
              x1=${x1} y1=${y1} x2=${x2} y2=${y2}/>
      `);

      // 4 minor ticks between each major.
      if (i < totalSteps) {
        for (let j = 1; j <= 4; j++) {
          const subRatio = (value + (opts.majorStep * j) / 5) / opts.max;
          const subDeg = DIAL.start + subRatio * DIAL.span;
          const subRed = opts.redFrom != null && value + (opts.majorStep * j) / 5 >= opts.redFrom;
          const [sx1, sy1] = polar(DIAL.rTickInner, subDeg);
          const [sx2, sy2] = polar(DIAL.rTickOuter, subDeg);
          ticks.push(svg`
            <line class=${`tick ${subRed ? "tick-red" : ""}`}
                  x1=${sx1} y1=${sy1} x2=${sx2} y2=${sy2}/>
          `);
        }
      }

      if (i % opts.labelStep === 0) {
        const [nx, ny] = polar(DIAL.rNumber, deg);
        const labelText =
          opts.key === "rpm" ? String(value / 1000) : String(value);
        ticks.push(svg`
          <text class=${`num ${isRed ? "num-red" : ""}`} x=${nx} y=${ny}>${labelText}</text>
        `);
      }
    }

    return svg`
      <svg viewBox="0 0 140 140">
        <!-- glowing outer halo -->
        <circle class="halo" cx=${DIAL.cx} cy=${DIAL.cy} r=${DIAL.rGlow}/>
        <!-- bezel ring (thin LED-like edge) -->
        <circle class="bezel" cx=${DIAL.cx} cy=${DIAL.cy} r=${DIAL.rBezel}/>
        <!-- recessed face (subtle radial gradient under the digit) -->
        <circle class="face" cx=${DIAL.cx} cy=${DIAL.cy} r=${DIAL.rFace}/>
        <circle class="face-rim" cx=${DIAL.cx} cy=${DIAL.cy} r=${DIAL.rFace}/>
        <!-- tick marks + numbers -->
        ${ticks}
        <!-- inner arc: track + live coloured fill (no needle) -->
        <path class="track" d=${arcPath(DIAL.rArc, DIAL.start, DIAL.start + DIAL.span)}/>
        <path class="fill" id=${`${opts.key}-fill`}
              d=${arcPath(DIAL.rArc, DIAL.start, DIAL.start + DIAL.span)}/>
      </svg>
    `;
  }

  render() {
    return html`
      <div class="card" data-stale="true">
        <span class="tick-dot"></span>

        <!-- Left dial: Speed -->
        <div class="dial" data-key="speed">
          ${this.renderDialSvg({
            key: "speed",
            max: MAX_SPEED,
            majorStep: 20,
            labelStep: 1,
          })}
          <div class="dial-text">
            <div class="dial-val" id="speed-val">0</div>
            <div class="dial-key">MPH</div>
          </div>
        </div>

        <!-- Center cluster -->
        <div class="center">
          ${this.renderRow({
            key: "throttle",
            label: "Throttle",
            // Gauge-with-needle (matches the app's throttle icon) — dial arc
            // + needle, in the web player's stroke style.
            iconPath: "M12 14 L16 10 M3.34 19 a10 10 0 1 1 17.32 0",
            unit: "%",
          })}
          ${this.renderRow({
            key: "fuel",
            label: "Fuel",
            iconPath:
              "M3 22h11V3a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v19zM7 7h5M17 8l3 3v9a2 2 0 0 1-4 0v-5a2 2 0 0 0-2-2h0M17 5v3",
            unit: "%",
          })}
          ${this.renderRow({
            key: "coolant",
            label: "Coolant",
            iconPath:
              "M12 2a2 2 0 0 0-2 2v10.5a4 4 0 1 0 4 0V4a2 2 0 0 0-2-2zM12 14V8",
            unit: "°C",
          })}
          ${this.renderGpsRow()}
        </div>

        <!-- Right dial: RPM -->
        <div class="dial" data-key="rpm">
          ${this.renderDialSvg({
            key: "rpm",
            max: MAX_RPM,
            majorStep: 1000,
            labelStep: 1,
            redFrom: REDLINE_RPM,
          })}
          <div class="dial-text">
            <div class="dial-val" id="rpm-val">0</div>
            <div class="dial-key">RPM ×1000</div>
          </div>
        </div>

        <!-- Shared gradient defs (used by both dials) -->
        <svg width="0" height="0" style="position:absolute;pointer-events:none;">
          <defs>
            <!-- Outer halo: radial fade — glow at the bezel edge, transparent core -->
            <radialGradient id="rk-halo-grad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stop-color="rgba(56,189,248,0)"/>
              <stop offset="78%" stop-color="rgba(56,189,248,0)"/>
              <stop offset="92%" stop-color="rgba(56,189,248,0.35)"/>
              <stop offset="100%" stop-color="rgba(56,189,248,0)"/>
            </radialGradient>
            <!-- Inner face: subtle dark radial gradient -->
            <radialGradient id="rk-face-grad" cx="50%" cy="42%" r="60%">
              <stop offset="0%" stop-color="rgba(20,30,48,0.85)"/>
              <stop offset="100%" stop-color="rgba(4,8,16,0.95)"/>
            </radialGradient>
            <linearGradient id="rk-grad-speed" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#38bdf8"/>
              <stop offset="60%" stop-color="#7dd3fc"/>
              <stop offset="100%" stop-color="#a855f7"/>
            </linearGradient>
            <linearGradient id="rk-grad-rpm" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#38bdf8"/>
              <stop offset="55%" stop-color="#7dd3fc"/>
              <stop offset="78%" stop-color="#fbbf24"/>
              <stop offset="100%" stop-color="#f43f5e"/>
            </linearGradient>
          </defs>
        </svg>
      </div>

      <!-- Mobile-only HUD — kvs20 flat layout. Hidden on desktop. -->
      <div class="hud-mobile" data-stale="true">
        <div class="m-row">
          <!-- Speed block (text only) -->
          <div class="m-speed-block">
            <div class="m-speed-label">SPEED</div>
            <div class="m-speed-line">
              <div class="m-speed-val" id="m-speed-val">--</div>
              <div class="m-speed-unit">mph</div>
            </div>
            <div class="m-stale-pill" id="m-stale-pill" hidden>STALE</div>
          </div>

          <!-- RPM gauge (half-circle SVG with needle, kvs20 style) -->
          <div class="m-rpm-block">
            <svg class="m-rpm-svg" viewBox="0 0 180 100" role="img" aria-label="RPM gauge">
              <path d="M 20 92 A 70 70 0 0 1 160 92" class="m-rpm-arc-bg"/>
              <path d="M 20 92 A 70 70 0 0 1 160 92" class="m-rpm-arc-fill" id="m-rpm-arc-fill"/>
              <g id="m-rpm-needle-group" class="m-rpm-needle-group" transform="rotate(-180 90 92)">
                <line x1="90" y1="92" x2="150" y2="92" class="m-rpm-needle"/>
              </g>
              <circle cx="90" cy="92" r="4.2" class="m-rpm-dot"/>
            </svg>
            <div class="m-rpm-text"><span id="m-rpm-val">--</span> RPM</div>
          </div>

          <!-- Labeled telemetry rows -->
          <div class="m-rows">
            <div class="m-tele-row">
              <div class="m-tele-label">Throttle:</div>
              <div class="m-tele-val" id="m-throttle-val">--</div>
            </div>
            <div class="m-tele-row">
              <div class="m-tele-label">Fuel:</div>
              <div class="m-tele-val" id="m-fuel-val">--</div>
            </div>
            <div class="m-tele-row">
              <div class="m-tele-label">Coolant:</div>
              <div class="m-tele-val" id="m-coolant-val">--</div>
            </div>
            <div class="m-tele-row">
              <div class="m-tele-label">GPS:</div>
              <div class="m-tele-val" id="m-gps-val">--, --</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderRow(opts: {
    key: "throttle" | "fuel" | "coolant";
    label: string;
    iconPath: string;
    unit: string;
  }) {
    return html`
      <div class="row" data-key=${opts.key} id=${`${opts.key}-row`}>
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d=${opts.iconPath}/>
        </svg>
        <span class="lbl">${opts.label}</span>
        <div class="bar" id=${`${opts.key}-bar`}></div>
        <span class="val"><span id=${`${opts.key}-val`}>0</span><span class="u">${opts.unit}</span></span>
      </div>
    `;
  }

  private renderGpsRow() {
    return html`
      <div class="row gps" data-key="gps">
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7z"/>
          <circle cx="12" cy="9" r="2.5"/>
        </svg>
        <span class="lbl">GPS</span>
        <span class="coords" id="gps-coords">Waiting for fix…</span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rk-hud": RkHud;
  }
}
